<template>
  <div id="page">
    <header>
      <h2>Mon Panier ({{ cartStore.getCount }})</h2>
      <div class="actions">
        <RouterLink to="/shop" class="btn btn-lg" :class="{ 'btn-ghost': cartStore.getCount > 0 }">
          <IconLibrary
            name="online-shop"
            :color="cartStore.getCount > 0 ? '' : 'white'"
            size="sm"
          />
          Continuer mes achats
        </RouterLink>
      </div>
    </header>
    <EmptyState v-if="cartStore.getCount < 1" class="boxed">
      <IconLibrary name="online-shop" size="xxl" />
      <h4>No Products in your Cart</h4>
      <p>
        Uh-oh, looks like you haven't added any products to your cart yet!<br />
        To get started, click on the following link
        <router-link to="/shop">to buy some products.</router-link>.
      </p>
      <p>
        <small>
          Once you've added your first products, they will appear in your cart and you finish the
          checkout.
        </small>
      </p>
    </EmptyState>
    <section v-else class="layout-flex">
      <main>
        <CartProductGroup
          v-for="GroupedProducts in groupedProducts"
          :key="GroupedProducts[0].sellerId"
          :products="GroupedProducts"
        />
        <FormTextArea
          title="forms.label.order_note"
          placeholder="forms.placeholder.order_note"
          v-model="orderNote"
          class="note"
        />
        <!-- Section Paiement -->
        <div class="payment-section">
          <PaymentMethod
            v-model="paymentMethod"
            :show-card-option="isSingleSeller"
            :seller-count="groupedProducts.length"
          />

          <CardPaymentForm
            v-if="paymentMethod === 'card' && isSingleSeller"
            :shipping-address="cartStore.buyer?.address"
            @process-payment="handleCardPayment"
          />
        </div>
      </main>
      <aside class="boxed">
        <div class="address">
          <h4>Livraison</h4>
          <BuyerSelector v-model="cartStore.buyer" v-if="userStore.user.role === 'director'" />
          <p>
            <strong>{{ cartStore.buyer?.identifier || cartStore.buyer?.company }}</strong>
            <br />
            <span>{{ cartStore.buyer?.full_name }}</span>
          </p>
          <p>
            {{ cartStore.buyer?.address?.address1 }}<br />
            {{ cartStore.buyer?.address?.address2 }}<br />
            <FlagLibrary :code="cartStore.buyer?.address?.country" />
            {{ cartStore.buyer?.address?.zip }}
            {{ cartStore.buyer?.address?.city }}
          </p>
        </div>
        <div>
          <h4>Récapitulatif</h4>
          <p class="flex">
            Sous-total (HT):
            <span class="money">{{ formatMoney({ amount: cartStore.getSubTotal }) }}</span>
          </p>
          <p class="flex" v-for="tax in cartStore.getDetailedTax.taxesBySeller">
            {{ $t('ui.vat') }} ({{ tax.taxRate }}% {{ $t('ui.of') }}
            {{ formatMoney({ amount: tax.subTotal }) }}):
            <span class="money">{{ formatMoney({ amount: tax.taxAmount }) }}</span>
          </p>
          <hr />
          <p class="flex">
            Total (TTC):
            <span class="money">{{
              formatMoney({ amount: cartStore.getDetailedTax.grandTotal || 0 })
            }}</span>
          </p>
          <div class="btn" @click="createOrder()" v-if="paymentMethod === 'bank_transfer'">
            Commander
          </div>
          <!-- <p class="info">
            <small>
              <strong>Délai d'expédition:</strong>
              Le fournisseur prévoit d’expédier votre commande dans les 8 jours ouvrables.
            </small>
          </p> -->
        </div>
      </aside>
    </section>
  </div>
</template>

<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { loadStripe } from '@stripe/stripe-js';
import { formatMoney, formatPrice } from '../libs/utils.js';
import { useCartStore } from '../stores/cart.js';
import { useOrderStore } from '../stores/orders.js';
import { usePersistCart } from '../composables/usePersistCart';
import { useUserStore } from '../stores/user.js';
import { paymentService } from '../services/paymentService.js';
import { kyWithAuth } from '../libs/ky.js';

import EmptyState from '../components/Shared/EmptyState.vue';
import FormTextArea from '../components/Shared/Forms/FormTextArea.vue';
import IconLibrary from '../components/Shared/IconLibrary.vue';
import FlagLibrary from '../components/Shared/FlagLibrary.vue';
import BuyerSelector from '../components/Shared/BuyerSelector.vue';
import CartProductGroup from '../components/Cart/CartProductGroup.vue';
import PaymentMethod from '../components/Payment/PaymentMethod.vue';
import CardPaymentForm from '../components/Payment/CardPaymentForm.vue';

usePersistCart();
const cartStore = useCartStore();
const orderStore = useOrderStore();
const userStore = useUserStore();
const orderNote = ref('');
const paymentMethod = ref('bank_transfer');
const orderReference = ref(`ORD-${Date.now()}`);
const stripe = ref(null);

const groupedProducts = computed(() => {
  const groups = {};
  cartStore.getCart.forEach((product) => {
    const sellerId = product.sellerId;
    if (!groups[sellerId]) {
      groups[sellerId] = [];
    }
    groups[sellerId].push(product);
  });
  return Object.values(groups);
});

const isSingleSeller = computed(() => {
  const sellerIds = [...new Set(cartStore.getCart.map((product) => product.sellerId))];
  return sellerIds.length === 1;
});

// Forcer le retour au virement bancaire si multi-seller et carte sélectionnée
watch(isSingleSeller, (newValue) => {
  if (!newValue && paymentMethod.value === 'card') {
    paymentMethod.value = 'bank_transfer';
  }
});

onMounted(async () => {
  try {
    // Initialiser Stripe
    const stripePublicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;
    if (stripePublicKey) {
      stripe.value = await loadStripe(stripePublicKey);
    }

    // I need the sellers to get the correct Tax rates.
    await cartStore.fetchSellers();
    await cartStore.fetchBuyer();
  } catch (e) {
    // TODO: Display some errors if the fetching of data fails.
    console.error(e);
  }
});

const router = useRouter();

const createOrderData = () => {
  const pos = cartStore.buyer;
  const toAddress = {
    pos_id: pos._id,
    identifier: pos.identifier,
    full_name: pos.full_name,
    phone: pos.phone,
    email: pos.email,
    ...pos.address
  };

  const shippingFees = cartStore.sellers.map((seller) => {
    const directorId = pos.role === 'buyer' ? pos.directors?.[0]?.user_id : pos._id;
    const matchedDirector = seller.directors.find(
      (director) => director.user_id.toString() === directorId.toString()
    );
    return {
      seller_id: seller._id,
      shipping_fee_included: matchedDirector ? matchedDirector.shipping_fee_included : false
    };
  });

  const products = cartStore.getCart?.map((product) => {
    const productData = {
      product_id: product.productId,
      variant_id: product.variantId || null,
      quantity: product.quantity,
      price: product.price,
      name: product.name,
      image: product.image,
      sku: product.sku,
      options: product.options,
      state: pos.option1 ? 'purchased' : 'pending'
    };

    // Only add seller_id if it exists (for in-house products, it may be undefined)
    if (product.sellerId) {
      productData.seller_id = product.sellerId;
    }

    return productData;
  });

  return {
    is_active: true,
    is_fulfilled: false,
    note: orderNote.value,
    tags: [],
    total_with_tax: formatPrice({ amount: cartStore.getDetailedTax.grandTotal }),
    taxes: cartStore.getDetailedTax.taxesBySeller.map((tax) => {
      const taxData = {
        subtotal: formatPrice({ amount: tax.subTotal }),
        tax_amount: formatPrice({ amount: tax.taxAmount }),
        tax_rate: tax.taxRate,
        total: formatPrice({ amount: tax.total })
      };

      // Only add seller_id if it exists (for in-house taxes, it may be undefined)
      if (tax.sellerId) {
        taxData.seller_id = tax.sellerId;
      }

      return taxData;
    }),
    shipping_fees: shippingFees,
    to_address: toAddress,
    products,
    order_reference: orderReference.value
  };
};

const createOrderForPayment = async () => {
  try {
    const orderData = {
      ...createOrderData(),
      is_paid: false,
      payment_method: 'card',
      payment_data: null
    };

    console.log('🐛 Creating order with data:', orderData);

    // Créer l'ordre sans vider le panier (on le fera après le paiement)
    const response = await kyWithAuth.post('orders', { json: orderData }).json();

    console.log('🐛 Order creation response:', response);

    if (!response.order || !response.order._id) {
      throw new Error("Réponse invalide de la création d'ordre");
    }

    const order = response.order;
    console.log('🐛 Order created with ID:', order._id);

    // Mettre à jour les quantités produits (comme dans orderStore.createOrder)
    for (let product of orderData.products) {
      console.log('🐛 Updating product quantity:', product.product_id, product.variant_id);
      if (product.variant_id && product.variant_id !== 'undefined') {
        await kyWithAuth
          .patch(`products/${product.product_id}/variants/${product.variant_id}`, {
            json: { quantity: product.quantity }
          })
          .json();
      }
    }

    // NE PAS vider le panier ici - on le fera après paiement réussi
    return { order: { _id: order._id } };
  } catch (error) {
    console.error('🐛 Erreur createOrderForPayment:', error);
    throw error;
  }
};

const createOrder = async (paymentData = null) => {
  try {
    const orderData = {
      ...createOrderData(),
      is_paid: paymentMethod.value === 'card' && paymentData?.success,
      payment_method: paymentMethod.value,
      payment_data: paymentData
    };

    const orderId = await orderStore.createOrder(orderData);
    router.push(`/shop/order/${orderId}/confirmation`);
  } catch (e) {
    console.error('Erreur createOrder:', e);
  }
};

const handleCardPayment = async (paymentInfo) => {
  try {
    console.log('Traitement du paiement par carte:', paymentInfo);

    // 1. Calculer le montant AVANT de créer l'ordre (pendant que le panier est encore plein)
    const amount = cartStore.getDetailedTax.grandTotal || cartStore.getSubTotal;

    console.log('🐛 AVANT création ordre - Cart grandTotal:', amount);
    console.log('🐛 AVANT création ordre - Cart items:', cartStore.getCart.length);
    console.log('🐛 AVANT création ordre - Cart subtotal:', cartStore.getSubTotal);

    if (!amount || amount <= 0) {
      throw new Error(`Montant invalide: ${amount}. Le panier semble vide.`);
    }

    // 2. Créer la commande pour obtenir l'orderId
    const orderData = await createOrderForPayment();
    if (!orderData?.order?._id) {
      throw new Error('Erreur lors de la création de la commande');
    }

    const orderId = orderData.order._id;
    console.log('🐛 OrderID créé:', orderId);

    // 2. Créer le Payment Intent via notre API
    const { clientSecret } = await paymentService.createPaymentIntent(orderId, amount, 'eur');

    // 3. Confirmer le paiement avec Stripe (automatic confirmation)
    const { error: confirmError, paymentIntent } = await stripe.value.confirmCardPayment(
      clientSecret,
      {
        payment_method: paymentInfo.paymentMethod.id
      }
    );

    if (confirmError) {
      console.error('Erreur confirmation Stripe:', confirmError);
      throw new Error(confirmError.message);
    }

    console.log('🎉 Paiement Stripe confirmé:', paymentIntent.status);

    // 4. Avec automatic confirmation, vérifier juste le statut final
    if (paymentIntent.status === 'succeeded') {
      console.log('Paiement confirmé avec succès !');

      // 5. Mettre à jour la commande avec les données de paiement via notre API
      try {
        const confirmResult = await paymentService.confirmPayment(paymentIntent.id, orderId);
        console.log('✅ Commande confirmée côté serveur:', confirmResult);
      } catch (updateError) {
        console.error('⚠️ Erreur confirmation serveur (mais paiement OK):', updateError);
        // Ne pas faire échouer le paiement si la mise à jour échoue
      }

      // Nettoyer le panier MAINTENANT (après paiement réussi)
      cartStore.cleanup();
      router.push(`/shop/order/${orderId}/confirmation`);
    } else {
      throw new Error(`Paiement non confirmé. Statut: ${paymentIntent.status}`);
    }
  } catch (error) {
    console.error('Erreur lors du paiement:', error);
    // TODO: Afficher une erreur à l'utilisateur
    alert(`Erreur: ${error.message}`);
  }
};
</script>

<style scoped lang="scss">
#page {
  padding: 0px !important;
}
#page > header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  h2 {
    margin: 0;
  }
}
:deep(input[type='number']) {
  background-color: $white;
  border: 2px solid rgba($brand-blue, 0.3);
  color: $tuatara;
  transition: border 200ms ease, color 200ms ease, transform 200ms ease-in-out;
  font-size: 16px;
}
.layout-flex {
  display: flex;
  gap: 35px;
}
.info {
  font-size: 85%;
  color: $brand-grey;
}
.product + .product {
  margin-top: 20px;
}
.address strong,
.address span {
  color: $brand-light-purple;
}

.address {
  strong,
  span {
    color: $brand-light-purple;
  }
  p:last-child {
    margin-bottom: 0;
  }
  margin-bottom: 40px;
}
.product {
  display: grid;
  grid-template-columns: 24px 65px 1fr 90px 90px;
  gap: 25px;
  align-items: center;
  .img-wrapper {
    width: 65px;
    height: 65px;
    overflow: hidden;
    box-shadow: 0px 0px 8px rgba($black, 0.1);
    border-radius: $base-radius;
    border: 2px solid $white;
    background-color: $white;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}
h4 {
  margin: 25px 0;
}
aside h4 {
  margin-top: 0;
}
.tag {
  padding: 3px 12px;
  background-color: $brand-light-purple;
  color: rgba($white, 0.8);
  border-radius: $base-radius;
  font-size: 90%;
  &.active {
    color: $white;
    background-color: $brand-blue;
  }
}
.note {
  margin-top: 2rem;
}

.payment-section {
  margin: 2rem 0;
  padding: 1.5rem;
  background: rgba($brand-blue, 0.02);
  border-radius: $base-radius;
  border: 1px solid rgba($brand-blue, 0.1);
}
main {
  width: 65%;
}

aside {
  width: 35%;
}
@media (max-width: 1023px) {
  .layout-flex {
    gap: 25px;
  }
  aside,
  main {
    width: 100%;
  }
}

@media (max-width: 767px) {
  .layout-flex {
    flex-direction: column;
  }
  .product {
    grid-template-columns: 24px 50px 1fr;
    grid-template-rows: auto auto;
    gap: 0.5rem 1.5rem;
    .img-wrapper {
      width: 50px;
      height: 50px;
    }
  }
  .product + .product {
    border-top: 1px solid $brand-light-purple;
    @include padding(xl, top);
  }
  .product .form-input {
    width: 100%;
  }
  .product > :nth-child(4),
  .product > :nth-child(5) {
    grid-column: span 2;
  }

  .product > :nth-child(4) {
    grid-column-start: 1;
  }

  .product > :nth-child(5) {
    grid-column-start: 3;
  }
  #page > header {
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    @include margin(xl, bottom);
    h2 {
      margin: 0;
    }
  }
}
</style>
